import React, { useEffect, useState } from 'react';

import formSchema from './schema';
import { useGlobal } from '../../../contexts/Global/context';
import Radio from '../../../components/Inputs/Radio';
import FormContainer from '../../../components/FormContainer';
import RelatorioCongenere from './RelatorioCongenere';
import Grid from './Grid';

import PesquisaContrato from './PesquisaContrato';
import PesquisaMunicipio from './PesquisaMunicipio';

import { cloneObject } from '../../../utils';
import {
    getCongenerByContract,
    getStates,
    getCities,
    getCongenerByContractAndPlan,
    getCongenerByContractAndPlanAndUf,
    getCongenerByContractAndPlanAndUfAndCity,
} from '../../../services';

import { PageContainer } from '../../../styles/styles';

const BY_CONTRACT = '1';

function mapStateOptions(results) {
    return results.reduce((states, { locaisAtendimentos }) => {
        locaisAtendimentos.forEach(({ siglaEstado, descricaoEstado }) => {
            states.push({
                codigo: siglaEstado,
                nome: descricaoEstado,
            });
        });

        return states;
    }, []);
}

function mapCityOptions(results) {
    return results.reduce((cities, { locaisAtendimentos }) => {
        locaisAtendimentos.forEach(({ codigoMunicipio, nomeMunicipio }) => {
            cities.push({
                codigo: codigoMunicipio,
                nome: nomeMunicipio,
            });
        });

        return cities;
    }, []);
}

function PesquisaCongeneres() {
    const { dataActiveContract } = useGlobal();
    const [formData, setFormData] = useState({
        ...cloneObject(formSchema),
        healthPlan: {
            ...formSchema.healthPlan,
            options: dataActiveContract.planos,
        },
    });
    const [dataTable, setDataTable] = useState([]);
    const [selectedCongener, setSelectedCongener] = useState();
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [contractSearched, setContractSearched] = useState('');

    async function selectRow(row) {
        const { congenere } = await getStates({ idContratoCongenere: row.numeroContratoCongenere });

        const uniqueStates = Array.from(
            new Set(congenere.estados.map(({ idUf, descricaoUf }) => JSON.stringify({ idUf, descricaoUf })))
        );

        const stateNames = [];
        let cityNames = [];

        await Promise.all(
            uniqueStates.map(async (estado) => {
                const { idUf, descricaoUf } = JSON.parse(estado);
                stateNames.push(descricaoUf);

                const stateResponse = await getCities({
                    idContratoCongenere: row.numeroContratoCongenere,
                    idUf,
                });
                cityNames = stateResponse?.congenere?.municipios.map(
                    ({ descricaoMunicipio }) => `${descricaoMunicipio}/${idUf}`
                );
            })
        );
        setSelectedCongener({ ...row, state: stateNames, city: Array.from(cityNames) });
    }

    async function handleSearchByContract(numeroContrato) {
        setLoading(true);
        setSearched(false);
        setSelectedCongener(null);

        const contractNumber = cloneObject(formData.contractNumber);
        contractNumber.value = numeroContrato;
        setFormData({ ...formData, contractNumber });

        const response = await getCongenerByContract({ numeroContrato });

        setLoading(false);
        setDataTable(response?.results || []);
        setSearched(true);
        setContractSearched(numeroContrato);
    }

    async function handleSearchByState() {
        setLoading(true);
        setSearched(false);
        setSelectedCongener(null);

        if (formData && formData.state?.value && formData.city?.value) {
            const response = await getCongenerByContractAndPlanAndUfAndCity({
                numeroContrato: dataActiveContract.numero,
                codigoPlano: formData?.healthPlan?.value,
                siglaUf: formData?.state?.value,
                codigoMunicipio: formData?.city?.value,
            });

            setDataTable(response?.results || []);
        }

        setLoading(false);
        setSearched(true);
    }

    async function handleChangeInput(event) {
        const { id, value, ...rest } = event;

        const field = { ...formData[id], value, error: '', ...rest };

        let editedForm = { ...formData, [id]: field };

        if (id === 'healthPlan') {
            const response =
                (await getCongenerByContractAndPlan({
                    numeroContrato: dataActiveContract.numero,
                    codigoPlano: value,
                })) || [];

            const state = cloneObject(formData.state);
            state.options = mapStateOptions(response?.results);
            state.value = '';
            state.lookup = '';
            editedForm = { ...editedForm, state };
        }

        if (id === 'state') {
            const response =
                (await getCongenerByContractAndPlanAndUf({
                    numeroContrato: dataActiveContract.numero,
                    codigoPlano: formData.healthPlan.value,
                    siglaUf: value,
                })) || [];

            const city = cloneObject(formData.city);
            city.options = mapCityOptions(response?.results);
            city.value = '';
            city.lookup = '';
            editedForm = { ...editedForm, city };
        }

        setDataTable([]);
        setSearched(false);
        setSelectedCongener(null);
        setFormData(editedForm);
    }

    function isByContract() {
        return formData.type.value === BY_CONTRACT;
    }

    useEffect(() => {
        setContractSearched('');
    }, [formData.type.value]);

    return (
        <PageContainer styles={{ paddingRight: 35, paddingLeft: 32 }}>
            <FormContainer>
                <Radio
                    style={{ whiteSpace: 'pre-wrap', marginTop: '24px', marginLeft: '8px' }}
                    field={formData.type}
                    onChange={handleChangeInput}
                />
            </FormContainer>
            {isByContract() ? (
                <PesquisaContrato formData={formData} handleSearch={handleSearchByContract} />
            ) : (
                <PesquisaMunicipio
                    formData={formData}
                    handleChangeInput={handleChangeInput}
                    handleSearch={handleSearchByState}
                />
            )}
            {!loading && !selectedCongener ? (
                <Grid
                    selectRow={selectRow}
                    setDataTable={setDataTable}
                    dataTable={dataTable}
                    loading={loading}
                    alreadySearched={searched}
                />
            ) : null}
            {selectedCongener ? (
                <RelatorioCongenere congener={selectedCongener} contractSearched={contractSearched} />
            ) : null}
        </PageContainer>
    );
}

export default PesquisaCongeneres;
